import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Settings, Mail, DollarSign, Save, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: 'Snow Exchange',
    support_email: 'support@snow-exchange.com',
    from_email: 'noreply@snow-exchange.com',
    trial_duration_days: 14,
    rm_pricing: {
      starter: { monthly: 49, annual: 470 },
      advanced: { monthly: 149, annual: 1430 },
      professional: { monthly: 349, annual: 3350 }
    },
    pm_pricing: {
      starter: { monthly: 49, annual: 470 },
      advanced: { monthly: 149, annual: 1430 },
      professional: { monthly: 349, annual: 3350 }
    }
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Merge response with defaults to handle missing fields
      const data = response.data;
      setPlatformSettings(prev => ({
        ...prev,
        platform_name: data.platform_name || prev.platform_name,
        support_email: data.support_email || prev.support_email,
        from_email: data.from_email || prev.from_email,
        trial_duration_days: data.trial_duration_days ?? prev.trial_duration_days,
        rm_pricing: data.rm_pricing || prev.rm_pricing,
        pm_pricing: data.pm_pricing || prev.pm_pricing
      }));
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Use defaults on error - don't show toast for initial load
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    const token = localStorage.getItem('token');
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, platformSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${section} settings saved successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const updateRmPricing = (tier, type, value) => {
    setPlatformSettings(prev => ({
      ...prev,
      rm_pricing: {
        ...prev.rm_pricing,
        [tier]: {
          ...prev.rm_pricing[tier],
          [type]: parseInt(value) || 0
        }
      }
    }));
  };

  const updatePmPricing = (tier, type, value) => {
    setPlatformSettings(prev => ({
      ...prev,
      pm_pricing: {
        ...prev.pm_pricing,
        [tier]: {
          ...prev.pm_pricing[tier],
          [type]: parseInt(value) || 0
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#62D84E' }} />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Platform Settings</h1>
          <p className="text-gray-600 mt-2">Configure platform-wide settings and preferences</p>
        </div>
        <Button variant="outline" onClick={fetchSettings} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="space-y-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
              General Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="platform_name">Platform Name</Label>
              <Input
                id="platform_name"
                value={platformSettings.platform_name}
                onChange={(e) => setPlatformSettings({ ...platformSettings, platform_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="trial_duration">Trial Duration (days)</Label>
              <Input
                id="trial_duration"
                type="number"
                value={platformSettings.trial_duration_days}
                onChange={(e) => setPlatformSettings({ ...platformSettings, trial_duration_days: parseInt(e.target.value) || 14 })}
              />
            </div>
            <Button onClick={() => handleSave('General')} style={{ backgroundColor: '#62D84E' }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save General Settings
            </Button>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
              Email Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="support_email">Support Email</Label>
              <Input
                id="support_email"
                type="email"
                value={platformSettings.support_email}
                onChange={(e) => setPlatformSettings({ ...platformSettings, support_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="from_email">From Email (Notifications)</Label>
              <Input
                id="from_email"
                type="email"
                value={platformSettings.from_email}
                onChange={(e) => setPlatformSettings({ ...platformSettings, from_email: e.target.value })}
              />
            </div>
            <Button onClick={() => handleSave('Email')} style={{ backgroundColor: '#62D84E' }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Email Settings
            </Button>
          </CardContent>
        </Card>

        {/* Pricing Settings - Resource Manager */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
              RM Subscription Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Starter (1-3 resources)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.starter.monthly}
                      onChange={(e) => updateRmPricing('starter', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.starter.annual}
                      onChange={(e) => updateRmPricing('starter', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Advanced (4-10 resources)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.advanced.monthly}
                      onChange={(e) => updateRmPricing('advanced', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.advanced.annual}
                      onChange={(e) => updateRmPricing('advanced', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Professional (11+ resources)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.professional.monthly}
                      onChange={(e) => updateRmPricing('professional', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.rm_pricing.professional.annual}
                      onChange={(e) => updateRmPricing('professional', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Button onClick={() => handleSave('RM Pricing')} style={{ backgroundColor: '#62D84E' }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save RM Pricing
            </Button>
          </CardContent>
        </Card>

        {/* Pricing Settings - Project Manager */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
              PM Subscription Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Starter (1-3 projects)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.starter.monthly}
                      onChange={(e) => updatePmPricing('starter', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.starter.annual}
                      onChange={(e) => updatePmPricing('starter', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Advanced (4-10 projects)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.advanced.monthly}
                      onChange={(e) => updatePmPricing('advanced', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.advanced.annual}
                      onChange={(e) => updatePmPricing('advanced', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium mb-3">Professional (11+ projects)</h4>
                <div className="space-y-2">
                  <div>
                    <Label>Monthly (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.professional.monthly}
                      onChange={(e) => updatePmPricing('professional', 'monthly', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Annual (€)</Label>
                    <Input
                      type="number"
                      value={platformSettings.pm_pricing.professional.annual}
                      onChange={(e) => updatePmPricing('professional', 'annual', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Button onClick={() => handleSave('PM Pricing')} style={{ backgroundColor: '#62D84E' }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save PM Pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
